<script setup lang="ts">
import {computed} from 'vue';
import dayjs from 'dayjs';
import {useI18n} from 'vue-i18n';
import {useProfileDateFormat} from '@/ts/composables/stateful/use-profile-date-format';
import type {BadgeHistoryItemDTO} from '@/ts/types/dto/badge-dto';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {buildImageTransformUrl} from '@/ts/utils/pure-functions';

const props = defineProps<{
  item: BadgeHistoryItemDTO;
}>();

const {t} = useI18n();
const patterns = useProfileDateFormat();

const earnedCount = computed(() => props.item.earnedTimes.length);

// 92px box; width budget is 2x for hi-DPI. Handles UUIDs and full Directus URLs alike
const badgeImageUrl = computed(() => buildImageTransformUrl(props.item.badge.imageUrl, {width: 192, format: 'webp'}));

// Numeric as it always was here, but ordered by the profile's country rather than the reading
// language: `Intl` with the locale put the month first for every English reader, wherever they
// live (IPD-3229).
function formatDateISOToLocale(iso: string): string {
  return dayjs(iso).format(patterns.value.numericDate);
}

const firstEarnedDate = computed(() => {
  const times = props.item.earnedTimes;
  if (!times.length) {return null;}
  const earliestTs = Math.min(...times.map(d => new Date(d).getTime()));
  return formatDateISOToLocale(new Date(earliestTs).toISOString());
});
</script>

<template>
  <div
    :class="{
      'badge-card': item.isCompleted,
      'badge-card-locked': !item.isCompleted
    }"
    role="article"
    :aria-label="item.badge.title"
  >
    <div class="badge-top">
      <div class="badge-header">
        <div class="badge-image-container">
          <q-img
            :src="badgeImageUrl"
            :alt="item.badge.title"
            class="badge-image"
            fit="contain"
          >
            <template #loading>
              <q-skeleton class="badge-image-loading" type="QAvatar"/>
            </template>
          </q-img>
        </div>

        <div v-if="item.isCompleted" class="badge-earned-info">
          <div class="badge-check" aria-hidden="true">
            <svg-illustration name="check.svg" inner-class="checkmark-icon"/>
          </div>
          <div class="badge-earned-count">
            {{ t('earned_multiplier', {count: earnedCount}) }}
          </div>
        </div>
      </div>
    </div>

    <div class="badge-content">
      <div class="badge-main-content">
        <h3 class="badge-title">
          {{ item.badge.title }}
        </h3>

        <p v-if="item.badge.description" class="badge-description">
          {{ item.badge.description }}
        </p>
      </div>

      <div v-if="item.isCompleted && firstEarnedDate" class="badge-metadata">
        <span class="first-earned">
          {{ t('badge_first_earned', {date: firstEarnedDate}) }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.badge-card {
  display: flex;
  height: auto;
  min-height: 350px;
  padding: var(--Spacing-spacing-7) var(--Spacing-spacing-6) var(--Spacing-spacing-7) var(--Spacing-spacing-6);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--Spacing-spacing-5);
  flex: 1 0 0;
  border-radius: var(--card-border-radius, 12px);
  background: var(--Neutral-UI-surface, var(--Light-Scheme-Neutral-UI-neutral-ui-10));
  position: relative;
  transition: transform 0.2s ease;

  @include screen-md {
    min-height: 400px;
  }

  @include screen-lg {
    min-height: 450px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .badge-card { transition: none; }
}

:root[data-theme='dark'] :where(.badge-card) {
  background: var(--Dark-Scheme-Neutral-UI-neutral-ui-10);
}

.badge-card-locked {
  display: flex;
  height: auto;
  min-height: 249px;
  padding: var(--Spacing-spacing-7) var(--Spacing-spacing-6) var(--Spacing-spacing-7) var(--Spacing-spacing-6);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--Spacing-spacing-5);
  flex: 1 0 0;
  border-radius: var(--card-border-radius, 12px);
  position: relative;

  @include screen-md {
    min-height: 282px;
  }

  @include screen-lg {
    min-height: 316px;
  }

  .badge-image-container { filter: grayscale(100%); }
}

.badge-top {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  width: 100%;
}

.badge-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.badge-image-container {
  width: var(--Spacing-spacing-10, 92px);
  height: var(--Spacing-spacing-10, 92px);
  display: flex;
  align-items: center;
  justify-content: center;

  .badge-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.badge-image-loading {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-20) !important;
}

.badge-earned-info {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-2);
}

.badge-check {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--Functional-wl-completed-surface);
  border-radius: 50%;
  padding: 6px;
  width: 28px;
  height: 28px;
  flex-shrink: 0;

  .checkmark-icon {
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-0) !important;
    width: 16px;
    height: 16px;
  }
}

.badge-earned-count {
  @include font-metric-md;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-70) !important;
  font-size: 18px;

  @include screen-md { font-size: 20px; }
  @include screen-lg { font-size: 24px; }
}

.badge-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  width: 100%;
}

.badge-main-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);
}

.badge-content .badge-title {
  @include font-display-5;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-100) !important;
  margin: 0;
}

.badge-content .badge-description {
  @include font-body-sm;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-60) !important;
  margin: 0;
}

.badge-content .badge-metadata .first-earned {
  @include font-caption_sm;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-60) !important;
}

.badge-card-locked .badge-content .badge-title {
  color: var(--Neutral-UI-neutral-ui-60) !important;
}

.badge-card-locked .badge-content .badge-description {
  color: var(--Neutral-UI-neutral-ui-60) !important;
}

.badge-card-locked .badge-earned-count,
.badge-card-locked .badge-content .badge-metadata .first-earned {
  color: var(--Neutral-UI-neutral-ui-60) !important;
}
</style>
